import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const fallbackSiteUrl = process.env.REACT_APP_SITE_URL?.trim() || 'https://www.myeunoia.online';
const fallbackSupabaseUrl = 'http://localhost:54321';
const fallbackSupabaseAnonKey = 'missing-anon-key';
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

const resolveAuthRedirectUrl = (query: string) => {
  const base =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : fallbackSiteUrl;

  return new URL(`/${query}`, base).toString();
};

if (!isSupabaseConfigured) {
  // eslint-disable-next-line no-console
  console.warn('Supabase credentials not found. Authentication will be disabled.');
}

export const supabase = createClient(
  supabaseUrl || fallbackSupabaseUrl,
  supabaseAnonKey || fallbackSupabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // Force fresh tokens on every request
      flowType: 'pkce',
    },
  },
);

// Auth helper functions
export const auth = {
  // Sign up with email and password
  signUp: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: resolveAuthRedirectUrl('?auth_flow=signup'),
      },
    });
    return { data, error };
  },

  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  resetPasswordForEmail: async (email: string) => {
    const redirectTo = resolveAuthRedirectUrl('?auth_flow=recovery');
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    return { error };
  },

  resendSignupEmail: async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: resolveAuthRedirectUrl('?auth_flow=signup'),
      },
    });
    return { error };
  },

  updatePassword: async (password: string) => {
    const { data, error } = await supabase.auth.updateUser({ password });
    return { data, error };
  },

  // Get current user
  getCurrentUser: () => {
    return supabase.auth.getUser();
  },

  // Get current session
  getSession: () => {
    return supabase.auth.getSession();
  },

  // Listen to auth state changes
  onAuthStateChange: (callback: (event: string, session: unknown) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Force clear all auth data and get fresh tokens
  forceRefresh: async () => {
    // eslint-disable-next-line no-console
    console.log('Forcing auth refresh...');
    // Clear all stored auth data
    await supabase.auth.signOut();
    // Clear localStorage manually
    localStorage.clear();
    sessionStorage.clear();
    // Force a page reload to get fresh tokens
    window.location.reload();
  },
};

export default supabase;
